import React, { useState, useEffect, useCallback } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';

import JiraLogin from './JiraLogin';
import JiraIssuesList from './JiraIssuesList';
import { useSaveJiraCode, useCheckJiraAuth } from './Jira.hooks';
import { filterMessageByStatus } from '../../common/filterMessageByStatus';
import AppSnackbar from '../../common/AppSnackbar';

type JiraListPageQueryParams = {
  code?: string;
  error_description?: string;
};

export type JiraListPageProps = RouteComponentProps<JiraListPageQueryParams>;

const JiraListPage: React.FC<JiraListPageProps> = (
  props: JiraListPageProps
) => {
  const { history, location } = props;
  const [codeResult, saveJiraCode] = useSaveJiraCode();
  const [checkResult, checkJiraAuth] = useCheckJiraAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const isJiraAuth: boolean = checkResult.data || false;
  const isLoading: boolean = codeResult.isLoading || checkResult.isLoading;

  // on mount
  useEffect(() => checkJiraAuth(), [checkJiraAuth]);

  useEffect(() => {
    // get token from query
    const params = new URLSearchParams(location.search);
    const error: string | null = params.get('error_description');
    setError(error || undefined);
    const code: string | null = params.get('oauth_verifier');
    code && saveJiraCode(code);
  }, [location.key, location.search, saveJiraCode]);

  useEffect(() => {
    verificationCode && saveJiraCode(verificationCode);
  }, [saveJiraCode, verificationCode]);

  useEffect(() => {
    codeResult.data && checkJiraAuth();
    if (window.opener) {
      window.opener.location.reload();
      window.close();
    }
  }, [codeResult.data, checkJiraAuth]);

  useEffect(() => {
    const error = checkResult.error || codeResult.error;
    if (error) {
      const { message, statusCode } = error;
      setError(
        filterMessageByStatus(message, statusCode, [401, 403, 404], null)
      );
    }
  }, [codeResult.error, checkResult.error]);

  useEffect(() => {
    if (isJiraAuth) {
      // remove code, error, error_description query params
      const params = new URLSearchParams(location.search);
      params.delete('code');
      params.delete('error');
      params.delete('error_description');
      let query = params.toString();
      query = query ? `?${query}` : '';
      history.replace(`${location.pathname}${query}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJiraAuth]);

  const onIssuesListError = useCallback((error: any) => {
    console.log('Issue List error', error);
    const { errorMessages, message } = error;
    const errMsg =
      message ||
      (errorMessages && errorMessages[0]) ||
      'Unable to fetch JIRA Issues.';
    setErrorMessage(errMsg);
  }, []);

  return (
    <>
      {error && !isJiraAuth && (
        <Alert severity='error' onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}
      <PageTemplate>
        <PageTemplateHeader>
          <h2>JIRA Issues</h2>
        </PageTemplateHeader>

        {isLoading && (
          <Box mt={2}>
            <Typography variant='body1'>Loading...</Typography>
          </Box>
        )}

        {isJiraAuth && !isLoading && (
          <PageTemplateCardOver>
            <JiraIssuesList onError={onIssuesListError} />
          </PageTemplateCardOver>
        )}

        {!isJiraAuth && !isLoading && (
          <PageTemplateDashboard>
            <JiraLogin
              isLoading={codeResult?.isLoading}
              onSubmit={setVerificationCode}
            />
          </PageTemplateDashboard>
        )}
      </PageTemplate>
      <AppSnackbar
        severity='error'
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage('')}
      >
        {errorMessage}
      </AppSnackbar>
    </>
  );
};

export default withRouter(JiraListPage);
